<template>
  <section class="results-view" data-testid="results-page">
    <h2 class="visually-hidden" tabindex="-1" data-testid="results-heading">
      Your tuition estimate
    </h2>
    <div class="mb-space-md border-bottom" role="tablist" aria-label="Academic year">
      <button
        v-for="(estimate, index) in props.estimates"
        :id="yearTabId(estimate.academic_year)"
        :key="estimate.academic_year"
        :ref="(element) => setYearTabRef(element, index)"
        type="button"
        role="tab"
        class="years-btn fw-bold"
        :class="{ 'active-year': index === activeYearIdx }"
        :aria-controls="activePanelId"
        :aria-selected="estimate.academic_year === selectedAcademicYear"
        :tabindex="estimate.academic_year === selectedAcademicYear ? 0 : -1"
        @click="selectYear(estimate.academic_year, index)"
        @keydown="handleYearKeydown($event, index)"
      >
        <span class="year-label">{{ formatAcademicYearLabel(estimate.academic_year) }}</span>
      </button>
    </div>

    <div class="row gx-space-md">
      <div
        class="results-overview pb-space-md mb-space-md border-bottom"
        data-testid="results-overview"
      >
        <div class="results-overview__content d-flex justify-content-between">
          <div class="pb-space-md pb-lg-0" tabindex="0">
            <p class="text-dark-2 mb-0">Your estimated academic year total:</p>
            <h2 class="h1-xl text-success" data-testid="estimated-year-total">
              {{ currencyOrZero(selectedEstimate.estimated_academic_year_total) }}
            </h2>
          </div>
          <div class="results-overview__metrics d-flex flex-column">
            <div
              class="result-metric py-space-xs px-space-sm border bg-light-1"
              data-testid="completion-time"
            >
              <svg
                class="result-metric__icon result-metric__icon--calendar result-metric__icon--desktop"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M3.77778 0C4.17031 0 4.48611 0.334375 4.48611 0.75V2H8.73611V0.75C8.73611 0.334375 9.05191 0 9.44444 0C9.83698 0 10.1528 0.334375 10.1528 0.75V2H11.3333C12.3752 2 13.2222 2.89687 13.2222 4V4.5V6H12.75H11.8056H8.5H1.41667V14C1.41667 14.275 1.62917 14.5 1.88889 14.5H8.3967C8.75972 15.0875 9.22309 15.5969 9.76319 16H1.88889C0.847049 16 0 15.1031 0 14V6V4.5V4C0 2.89687 0.847049 2 1.88889 2H3.06944V0.75C3.06944 0.334375 3.38524 0 3.77778 0ZM8.5 11.5C8.5 10.3065 8.94777 9.16193 9.7448 8.31802C10.5418 7.47411 11.6228 7 12.75 7C13.8772 7 14.9582 7.47411 15.7552 8.31802C16.5522 9.16193 17 10.3065 17 11.5C17 12.6935 16.5522 13.8381 15.7552 14.682C14.9582 15.5259 13.8772 16 12.75 16C11.6228 16 10.5418 15.5259 9.7448 14.682C8.94777 13.8381 8.5 12.6935 8.5 11.5ZM12.75 9C12.4903 9 12.2778 9.225 12.2778 9.5V11.5C12.2778 11.775 12.4903 12 12.75 12H14.1667C14.4264 12 14.6389 11.775 14.6389 11.5C14.6389 11.225 14.4264 11 14.1667 11H13.2222V9.5C13.2222 9.225 13.0097 9 12.75 9Z"
                  fill="#747474"
                />
              </svg>
              <p class="result-metric__text mb-0" tabindex="0">
                <span class="result-metric__label me-lg-space-xxxs"
                  >Estimated
                  <span class="result-metric__nowrap">completion time:</span>
                </span>
                <span class="result-metric__value">
                  <svg
                    class="result-metric__icon result-metric__icon--calendar result-metric__icon--mobile"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M3.77778 0C4.17031 0 4.48611 0.334375 4.48611 0.75V2H8.73611V0.75C8.73611 0.334375 9.05191 0 9.44444 0C9.83698 0 10.1528 0.334375 10.1528 0.75V2H11.3333C12.3752 2 13.2222 2.89687 13.2222 4V4.5V6H12.75H11.8056H8.5H1.41667V14C1.41667 14.275 1.62917 14.5 1.88889 14.5H8.3967C8.75972 15.0875 9.22309 15.5969 9.76319 16H1.88889C0.847049 16 0 15.1031 0 14V6V4.5V4C0 2.89687 0.847049 2 1.88889 2H3.06944V0.75C3.06944 0.334375 3.38524 0 3.77778 0ZM8.5 11.5C8.5 10.3065 8.94777 9.16193 9.7448 8.31802C10.5418 7.47411 11.6228 7 12.75 7C13.8772 7 14.9582 7.47411 15.7552 8.31802C16.5522 9.16193 17 10.3065 17 11.5C17 12.6935 16.5522 13.8381 15.7552 14.682C14.9582 15.5259 13.8772 16 12.75 16C11.6228 16 10.5418 15.5259 9.7448 14.682C8.94777 13.8381 8.5 12.6935 8.5 11.5ZM12.75 9C12.4903 9 12.2778 9.225 12.2778 9.5V11.5C12.2778 11.775 12.4903 12 12.75 12H14.1667C14.4264 12 14.6389 11.775 14.6389 11.5C14.6389 11.225 14.4264 11 14.1667 11H13.2222V9.5C13.2222 9.225 13.0097 9 12.75 9Z"
                      fill="#747474"
                    />
                  </svg>
                  <span class="fw-bold text-dark-3"
                    >{{ selectedEstimate.completion_time }} years</span
                  >
                </span>
              </p>
              <ToolTip
                class="result-metric__tooltip"
                :tooltip-width="260"
                :display-shadow="false"
                placement="right-start"
                :offset="8"
                tooltip-id="completion-time-tooltip"
                aria-label="About completion time"
              >
                <template #tooltip>
                  <div class="result-metric__tooltip-content">
                    {{ completionTimeTooltip }}
                  </div>
                </template>
                <FontAwesomeIcon
                  class="result-metric__info-icon"
                  icon="fa-solid fa-circle-info"
                  aria-hidden="true"
                />
              </ToolTip>
            </div>
            <!-- Optimizely reveals this metric by removing d-none from #estimated-total-cost. -->
            <div
              id="estimated-total-cost"
              class="result-metric d-none py-space-xs px-space-sm border bg-light-1"
              data-testid="estimated-total-cost"
            >
              <svg
                class="result-metric__icon result-metric__icon--dollar result-metric__icon--desktop"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M14.5 8C14.5 6.27609 13.8152 4.62279 12.5962 3.40381C11.3772 2.18482 9.72391 1.5 8 1.5C6.27609 1.5 4.62279 2.18482 3.40381 3.40381C2.18482 4.62279 1.5 6.27609 1.5 8C1.5 9.72391 2.18482 11.3772 3.40381 12.5962C4.62279 13.8152 6.27609 14.5 8 14.5C9.72391 14.5 11.3772 13.8152 12.5962 12.5962C13.8152 11.3772 14.5 9.72391 14.5 8ZM0 8C0 5.87827 0.842855 3.84344 2.34315 2.34315C3.84344 0.842855 5.87827 0 8 0C10.1217 0 12.1566 0.842855 13.6569 2.34315C15.1571 3.84344 16 5.87827 16 8C16 10.1217 15.1571 12.1566 13.6569 13.6569C12.1566 15.1571 10.1217 16 8 16C5.87827 16 3.84344 15.1571 2.34315 13.6569C0.842855 12.1566 0 10.1217 0 8ZM8.65 4.175V4.61875C8.95312 4.65625 9.25625 4.74062 9.55625 4.825C9.61562 4.84062 9.67188 4.85625 9.73125 4.875C10.0906 4.975 10.3031 5.34688 10.2031 5.70625C10.1031 6.06563 9.73125 6.275 9.37187 6.17812C9.32187 6.16563 9.275 6.15 9.225 6.1375C9.00625 6.075 8.7875 6.01562 8.56563 5.97188C8.15312 5.89375 7.675 5.93125 7.29063 6.09688C6.94688 6.24688 6.6625 6.60938 7.05312 6.85938C7.35938 7.05625 7.73438 7.15625 8.09062 7.25313C8.16562 7.27188 8.2375 7.29375 8.30937 7.3125C8.79688 7.45 9.41875 7.62813 9.88437 7.94688C10.4906 8.3625 10.775 9.0375 10.6406 9.7625C10.5125 10.4625 10.025 10.9219 9.44063 11.1594C9.19688 11.2594 8.93125 11.3219 8.65312 11.3531V11.8281C8.65312 12.2 8.35 12.5031 7.97813 12.5031C7.60625 12.5031 7.30312 12.2 7.30312 11.8281V11.2844C6.85 11.1813 6.40625 11.0375 5.96562 10.8938C5.6125 10.7781 5.41875 10.3937 5.5375 10.0406C5.65625 9.6875 6.0375 9.49375 6.39062 9.6125C6.46875 9.6375 6.54688 9.66563 6.625 9.69063C6.97813 9.80937 7.34062 9.93125 7.70312 9.99063C8.23438 10.0688 8.65938 10.0219 8.9375 9.90938C9.3125 9.75938 9.49062 9.3125 9.12187 9.0625C8.80625 8.84688 8.41562 8.74063 8.04375 8.64062C7.97187 8.62187 7.90313 8.60313 7.83125 8.58125C7.35938 8.44688 6.76875 8.28125 6.325 7.99687C5.71562 7.60625 5.40625 6.95625 5.5375 6.23438C5.6625 5.55312 6.19375 5.1 6.75625 4.85625C6.92812 4.78125 7.1125 4.72187 7.30312 4.67812V4.175C7.30312 3.80313 7.60625 3.5 7.97813 3.5C8.35 3.5 8.65312 3.80313 8.65312 4.175H8.65Z"
                  fill="currentColor"
                />
              </svg>
              <p class="result-metric__text mb-0" tabindex="0">
                <span class="result-metric__label me-lg-space-xxxs"
                  >Estimated
                  <span class="result-metric__nowrap">total investment: </span>
                </span>
                <span class="result-metric__value">
                  <svg
                    class="result-metric__icon result-metric__icon--dollar result-metric__icon--mobile"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M14.5 8C14.5 6.27609 13.8152 4.62279 12.5962 3.40381C11.3772 2.18482 9.72391 1.5 8 1.5C6.27609 1.5 4.62279 2.18482 3.40381 3.40381C2.18482 4.62279 1.5 6.27609 1.5 8C1.5 9.72391 2.18482 11.3772 3.40381 12.5962C4.62279 13.8152 6.27609 14.5 8 14.5C9.72391 14.5 11.3772 13.8152 12.5962 12.5962C13.8152 11.3772 14.5 9.72391 14.5 8ZM0 8C0 5.87827 0.842855 3.84344 2.34315 2.34315C3.84344 0.842855 5.87827 0 8 0C10.1217 0 12.1566 0.842855 13.6569 2.34315C15.1571 3.84344 16 5.87827 16 8C16 10.1217 15.1571 12.1566 13.6569 13.6569C12.1566 15.1571 10.1217 16 8 16C5.87827 16 3.84344 15.1571 2.34315 13.6569C0.842855 12.1566 0 10.1217 0 8ZM8.65 4.175V4.61875C8.95312 4.65625 9.25625 4.74062 9.55625 4.825C9.61562 4.84062 9.67188 4.85625 9.73125 4.875C10.0906 4.975 10.3031 5.34688 10.2031 5.70625C10.1031 6.06563 9.73125 6.275 9.37187 6.17812C9.32187 6.16563 9.275 6.15 9.225 6.1375C9.00625 6.075 8.7875 6.01562 8.56563 5.97188C8.15312 5.89375 7.675 5.93125 7.29063 6.09688C6.94688 6.24688 6.6625 6.60938 7.05312 6.85938C7.35938 7.05625 7.73438 7.15625 8.09062 7.25313C8.16562 7.27188 8.2375 7.29375 8.30937 7.3125C8.79688 7.45 9.41875 7.62813 9.88437 7.94688C10.4906 8.3625 10.775 9.0375 10.6406 9.7625C10.5125 10.4625 10.025 10.9219 9.44063 11.1594C9.19688 11.2594 8.93125 11.3219 8.65312 11.3531V11.8281C8.65312 12.2 8.35 12.5031 7.97813 12.5031C7.60625 12.5031 7.30312 12.2 7.30312 11.8281V11.2844C6.85 11.1813 6.40625 11.0375 5.96562 10.8938C5.6125 10.7781 5.41875 10.3937 5.5375 10.0406C5.65625 9.6875 6.0375 9.49375 6.39062 9.6125C6.46875 9.6375 6.54688 9.66563 6.625 9.69063C6.97813 9.80937 7.34062 9.93125 7.70312 9.99063C8.23438 10.0688 8.65938 10.0219 8.9375 9.90938C9.3125 9.75938 9.49062 9.3125 9.12187 9.0625C8.80625 8.84688 8.41562 8.74063 8.04375 8.64062C7.97187 8.62187 7.90313 8.60313 7.83125 8.58125C7.35938 8.44688 6.76875 8.28125 6.325 7.99687C5.71562 7.60625 5.40625 6.95625 5.5375 6.23438C5.6625 5.55312 6.19375 5.1 6.75625 4.85625C6.92812 4.78125 7.1125 4.72187 7.30312 4.67812V4.175C7.30312 3.80313 7.60625 3.5 7.97813 3.5C8.35 3.5 8.65312 3.80313 8.65312 4.175H8.65Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span class="fw-bold text-dark-3"
                    >{{ roundedUpCurrencyOrZero(selectedEstimate.estimated_total_cost) }}
                  </span>
                </span>
              </p>
              <ToolTip
                class="result-metric__tooltip"
                :tooltip-width="260"
                :display-shadow="false"
                placement="right-start"
                :offset="8"
                tooltip-id="estimated-total-cost-tooltip"
                aria-label="About estimated total cost"
              >
                <template #tooltip>
                  <div class="result-metric__tooltip-content">
                    {{ estimatedTotalCostTooltip }}
                  </div>
                </template>
                <FontAwesomeIcon
                  class="result-metric__info-icon"
                  icon="fa-solid fa-circle-info"
                  aria-hidden="true"
                />
              </ToolTip>
            </div>
          </div>
        </div>
      </div>

      <div v-if="tuitionBanner" class="col-12">
        <div
          class="tuition-banner mb-space-md"
          data-testid="tuition-banner"
          role="note"
          tabindex="0"
        >
          <FontAwesomeIcon
            class="tuition-banner__icon"
            icon="fa-solid fa-bell"
            aria-hidden="true"
          />
          <div
            ref="tuitionBannerMessageRef"
            class="tuition-banner__message"
            v-html="tuitionBanner"
          ></div>
        </div>
      </div>

      <div class="col-12 col-lg-4 mb-space-md" data-testid="summary-column">
        <div v-if="showInlineEdit" data-testid="edit-column">
          <h2 ref="editHeadingRef" class="visually-hidden" tabindex="-1" data-testid="edit-heading">
            Edit your selections
          </h2>
          <slot name="edit-form" />
          <div class="d-flex align-items-center flex-nowrap gap-space-xs">
            <button
              type="button"
              class="btn btn-secondary text-nowrap"
              data-testid="cancel-edit"
              @click="emit('cancel')"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-dark-3 d-inline-flex align-items-center gap-space-xs text-nowrap"
              data-testid="recalculate"
              @click="emit('recalculate')"
            >
              <FontAwesomeIcon icon="fa-solid fa-arrows-rotate" aria-hidden="true" />
              <span>Recalculate</span>
            </button>
          </div>
        </div>

        <div v-if="showCommittedSummary" data-testid="committed-summary">
          <div class="result-summary-list mb-space-md">
            <div
              data-testid="summary-degree-type"
              class="result-summary-row result-summary-row--degree d-lg-none mb-space-sm"
            >
              <p class="result-summary-label mb-0">Degree type</p>
              <p class="result-summary-value fw-bold">
                {{ props.selections.degreeType }}
              </p>
            </div>
            <div
              data-testid="summary-program"
              class="result-summary-row result-summary-row--program mb-space-sm"
            >
              <p class="result-summary-label mb-0">Program</p>
              <p class="result-summary-value fw-bold d-none d-lg-inline">
                {{ props.selections.degreeType }}
              </p>
              <p class="result-summary-value result-summary-value--programtitle fw-bold">
                {{ props.selections.program?.title ?? "" }}
              </p>
            </div>
            <div
              data-testid="summary-summer-sessions"
              class="result-summary-row result-summary-row--summer d-lg-none mb-space-sm"
            >
              <p class="result-summary-label mb-0">Summer sessions</p>
              <p class="result-summary-value fw-bold">
                {{ props.selections.includeSummer ? "Yes" : "No" }}
              </p>
            </div>
            <div
              v-if="showTransferCredits"
              data-testid="summary-transfer"
              class="result-summary-row result-summary-row--transfer mb-space-sm"
            >
              <p class="result-summary-label mb-0">Transfer credits</p>
              <p class="result-summary-value fw-bold">
                {{ props.selections.transferCredits ?? 0 }} credits
              </p>
            </div>
            <div
              data-testid="summary-credits"
              class="result-summary-row result-summary-row--credits mb-space-sm"
            >
              <p class="result-summary-label mb-0">
                <span class="d-lg-none">Credits per semester</span>
                <span class="d-none d-lg-inline">Credits per semester</span>
              </p>
              <p class="result-summary-value fw-bold">
                {{ props.selections.creditsPerSemester }}<span> credits</span>
              </p>
            </div>
            <div
              v-if="showResidency"
              data-testid="summary-residency"
              class="result-summary-row result-summary-row--residency mb-space-sm"
            >
              <p class="result-summary-label mb-0">Residency</p>
              <p class="result-summary-value fw-bold">{{ residencyLabel }}</p>
            </div>
            <div
              v-if="props.selections.degreeType === 'Undergraduate'"
              data-testid="summary-grants"
              class="result-summary-row result-summary-row--grants mb-space-sm"
            >
              <p class="result-summary-label mb-0">Grants</p>
              <p class="result-summary-value fw-bold">
                {{ wholeCurrencyOrZero(props.selections.grants) }}<span>/year</span>
              </p>
            </div>
            <div
              data-testid="summary-scholarships"
              class="result-summary-row result-summary-row--scholarships mb-space-sm"
            >
              <p class="result-summary-label mb-0">Scholarships</p>
              <p class="result-summary-value fw-bold">
                {{ wholeCurrencyOrZero(props.selections.scholarships) }}<span>/year</span>
              </p>
            </div>
            <div
              data-testid="summary-employer-benefit"
              class="result-summary-row result-summary-row--employer mb-space-sm"
            >
              <p class="result-summary-label mb-0">
                <span class="d-lg-none">Employee tuition benefit</span>
                <span class="d-none d-lg-inline">Employer tuition benefit</span>
              </p>
              <p class="result-summary-value fw-bold">
                {{ wholeCurrencyOrZero(props.selections.employerTuitionBenefit) }}<span>/year</span>
              </p>
            </div>
          </div>
          <button
            ref="editSelectionsButtonRef"
            type="button"
            class="btn btn-secondary result-edit-button"
            data-testid="edit-selections"
            @click="emit('edit')"
          >
            <FontAwesomeIcon icon="fa-solid fa-pen-to-square" aria-hidden="true" />
            <span>Edit your selections</span>
          </button>
        </div>
      </div>

      <div
        v-if="selectedEstimate"
        class="col-12 col-lg-8 position-relative"
        data-testid="result-region"
        :aria-busy="props.pending ? 'true' : 'false'"
        :aria-labelledby="yearPanelHeadingId(selectedEstimate.academic_year)"
      >
        <div
          :id="yearPanelId(selectedEstimate.academic_year)"
          role="tabpanel"
          tabindex="0"
          :aria-labelledby="yearTabId(selectedEstimate.academic_year)"
          data-testid="year-panel"
        >
          <div class="form-check mb-space-md d-flex justify-content-end">
            <input
              id="tuition-calculator-include-summer"
              class="form-check-input bg-secondary accent-dark"
              type="checkbox"
              data-testid="include-summer"
              :checked="props.selections.includeSummer"
              @change="handleIncludeSummerChange"
            />
            <label
              class="form-check-label fw-bold ms-space-xxs"
              for="tuition-calculator-include-summer"
            >
              Include summers
            </label>
          </div>

          <Alert
            v-if="props.requestError"
            :show="true"
            variant="warning"
            data-testid="results-error"
          >
            <p class="mb-space-xs">We could not update your estimate.</p>
            <button
              type="button"
              class="btn btn-secondary"
              data-testid="retry"
              @click="emit('retry')"
            >
              Retry
            </button>
          </Alert>

          <div v-else data-testid="term-breakdown">
            <div class="mb-space-md border-bottom" data-testid="term-items">
              <div class="mb-space-xs">
                <CostCollapseV2
                  :id="collapseId(selectedEstimate.academic_year, 'fall')"
                  data-testid="term-fall"
                  :title="formatTermLabel('fall', selectedEstimate.academic_year)"
                  bg-variant="light-2"
                  :amount="termAmount(selectedEstimate.academic_year, 'fall')"
                  :items="toCollapseItems(selectedEstimate.fall)"
                  @open="
                    handleCollapseOpen(formatTermLabel('fall', selectedEstimate.academic_year))
                  "
                  @close="
                    handleCollapseClose(formatTermLabel('fall', selectedEstimate.academic_year))
                  "
                />
              </div>
              <div class="mb-space-xs">
                <CostCollapseV2
                  :id="collapseId(selectedEstimate.academic_year, 'spring')"
                  data-testid="term-spring"
                  :title="formatTermLabel('spring', selectedEstimate.academic_year)"
                  bg-variant="light-2"
                  :amount="termAmount(selectedEstimate.academic_year, 'spring')"
                  :items="toCollapseItems(selectedEstimate.spring)"
                  @open="
                    handleCollapseOpen(formatTermLabel('spring', selectedEstimate.academic_year))
                  "
                  @close="
                    handleCollapseClose(formatTermLabel('spring', selectedEstimate.academic_year))
                  "
                />
              </div>
              <div class="mb-space-xs">
                <CostCollapseV2
                  v-if="props.selections.includeSummer && selectedEstimate.summer"
                  :id="collapseId(selectedEstimate.academic_year, 'summer')"
                  data-testid="term-summer"
                  :title="formatTermLabel('summer', selectedEstimate.academic_year)"
                  bg-variant="light-2"
                  :amount="termAmount(selectedEstimate.academic_year, 'summer')"
                  :items="toCollapseItems(selectedEstimate.summer)"
                  @open="
                    handleCollapseOpen(formatTermLabel('summer', selectedEstimate.academic_year))
                  "
                  @close="
                    handleCollapseClose(formatTermLabel('summer', selectedEstimate.academic_year))
                  "
                />
              </div>
            </div>

            <div class="result-totals" data-testid="result-totals">
              <div
                v-if="hasSavingsApplied"
                class="px-space-sm py-space-xs d-flex justify-content-between align-items-center fw-bold"
                data-testid="academic-year-total"
              >
                <span>Academic year total</span>
                <span>
                  {{ currencyOrZero(selectedEstimate.academic_year_total) }}
                </span>
              </div>
              <div v-if="hasSavingsApplied" class="result-savings-collapse">
                <button
                  :id="savingsAppliedButtonId"
                  type="button"
                  class="result-savings-toggle result-total--savings animated-box py-space-xs px-space-sm"
                  :class="{ active: animate }"
                  data-testid="savings-applied"
                  :aria-expanded="isSavingsExpanded"
                  :aria-controls="savingsAppliedRegionId"
                  @click="toggleSavingsApplied"
                >
                  <span class="result-savings-toggle__label">
                    <FontAwesomeIcon
                      class="result-savings-toggle__icon me-space-xs"
                      :icon="`fa-solid ${isSavingsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`"
                      aria-hidden="true"
                    />
                    <span>Savings applied</span>
                  </span>
                  <span class="result-savings-toggle__amount">
                    {{ currencyOrZero(selectedEstimate.savings_applied) }}
                  </span>
                </button>
                <div
                  v-show="isSavingsExpanded"
                  :id="savingsAppliedRegionId"
                  class="result-savings-body"
                  role="region"
                  :aria-labelledby="savingsAppliedButtonId"
                  data-testid="savings-applied-region"
                >
                  <div
                    v-for="item in savingsAppliedItems"
                    :key="item.title"
                    class="result-savings-item"
                  >
                    <p class="my-space-xxs">{{ item.title }}</p>
                    <p class="result-savings-item__amount my-space-xxs">
                      {{ item.amount }}
                    </p>
                  </div>
                </div>
              </div>
              <div
                class="d-flex justify-content-between align-items-center fw-bold px-space-sm py-space-xs"
                data-testid="estimated-total"
              >
                <span>Estimated academic year total</span>
                <span>
                  {{ currencyOrZero(selectedEstimate.estimated_academic_year_total) }}
                </span>
              </div>
            </div>

            <div class="d-flex justify-content-center">
              <NuxtLink
                to="/what-it-costs/financial-aid/"
                class="non-rds-link fw-bold result-fin-aid"
                data-testid="explore-fin-aid"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M15.5554 13.7706L12.4394 10.6553C12.2988 10.5147 12.1082 10.4366 11.9082 10.4366H11.3988C12.2613 9.33344 12.7738 7.94625 12.7738 6.43687C12.7738 2.84656 9.86415 -0.0625 6.27354 -0.0625C2.68293 -0.0625 -0.227051 2.84656 -0.227051 6.43687C-0.227051 10.0272 2.68262 12.9362 6.27354 12.9362C7.78291 12.9362 9.17071 12.4237 10.2738 11.5612V12.0706C10.2738 12.2706 10.352 12.4613 10.4926 12.6019L13.6085 15.7172C13.9023 16.0109 14.3773 16.0109 14.6679 15.7172L15.5522 14.8328C15.846 14.5391 15.846 14.0644 15.5554 13.7706ZM6.27354 10.9362C3.78793 10.9362 1.77356 8.92562 1.77356 6.43687C1.77356 3.95156 3.78449 1.9375 6.27354 1.9375C8.75915 1.9375 10.7735 3.94812 10.7735 6.43687C10.7735 8.92219 8.76259 10.9362 6.27354 10.9362ZM7.12073 6.16937L5.71417 5.7475C5.55292 5.69906 5.44011 5.53562 5.44011 5.34969C5.44011 5.1225 5.60573 4.9375 5.80886 4.9375H6.68729C6.82979 4.9375 6.96729 4.97781 7.08791 5.05375C7.18916 5.11719 7.31791 5.11344 7.40447 5.03094L7.77166 4.68062C7.88197 4.57531 7.87572 4.39281 7.75385 4.30125C7.46947 4.08781 7.12635 3.96469 6.77354 3.94656V3.4375C6.77354 3.29938 6.66166 3.1875 6.52354 3.1875H6.02354C5.88542 3.1875 5.77354 3.29938 5.77354 3.4375V3.94125C5.03511 3.96094 4.4398 4.58344 4.4398 5.34969C4.4398 5.97375 4.84574 6.53125 5.42667 6.70563L6.83323 7.1275C6.99448 7.17594 7.10729 7.33938 7.10729 7.52531C7.10729 7.7525 6.94166 7.9375 6.73854 7.9375H5.86042C5.71792 7.9375 5.58042 7.89719 5.4598 7.82125C5.35855 7.75781 5.2298 7.76156 5.14324 7.84406L4.77605 8.19438C4.66574 8.29969 4.67199 8.48219 4.79386 8.57375C5.07824 8.78719 5.42136 8.91031 5.77417 8.92844V9.4375C5.77417 9.57563 5.88604 9.6875 6.02417 9.6875H6.52417C6.66229 9.6875 6.77416 9.57563 6.77416 9.4375V8.93375C7.5126 8.91406 8.10791 8.29187 8.10791 7.52531C8.10791 6.90125 7.70197 6.34375 7.12073 6.16937Z"
                    fill="#8C0B42"
                  />
                </svg>
                <span>Explore financial aid options</span>
              </NuxtLink>
            </div>
            <div
              v-if="disclaimer"
              class="result-disclaimer bg-light-1 p-space-sm"
              data-testid="result-disclaimer"
              role="presentation"
              @click="handleFaqLink"
              v-html="disclaimer"
            ></div>
          </div>
        </div>

        <div
          v-if="props.pending"
          class="result-loader position-absolute d-flex justify-content-center"
          data-testid="result-loader"
          role="status"
          aria-live="polite"
        >
          <div class="result-loader__card" data-testid="result-loader-card">
            <p class="result-loader__title mb-0">Recalculating your costs</p>
            <div
              class="result-loader__spinner"
              data-testid="result-loader-spinner"
              aria-hidden="true"
            >
              <span v-for="dot in 5" :key="dot"></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Transition name="result-mobile-drawer">
      <div
        v-if="showMobileEditDrawer"
        class="result-mobile-drawer"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="mobileDrawerTitleId"
        data-testid="mobile-edit-drawer"
        @keydown.esc.stop.prevent="emit('cancel')"
      >
        <div class="result-mobile-drawer__backdrop" aria-hidden="true"></div>
        <div class="result-mobile-drawer__panel">
          <header class="result-mobile-drawer__header">
            <h2 :id="mobileDrawerTitleId" class="h5 mb-0">Edit your selections</h2>
            <button
              ref="mobileDrawerCloseButtonRef"
              type="button"
              class="result-mobile-drawer__close"
              aria-label="Close edit selections"
              data-testid="mobile-edit-close"
              @click="emit('cancel')"
            >
              <FontAwesomeIcon icon="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </header>

          <div class="result-mobile-drawer__body">
            <slot name="edit-form" />
          </div>

          <footer class="result-mobile-drawer__footer">
            <button
              type="button"
              class="btn btn-link fw-bold text-dark-3"
              data-testid="cancel-edit"
              @click="emit('cancel')"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-dark-3 d-inline-flex align-items-center gap-space-xs text-nowrap"
              data-testid="recalculate"
              @click="emit('recalculate')"
            >
              <FontAwesomeIcon icon="fa-solid fa-arrows-rotate" aria-hidden="true" />
              <span>Recalculate</span>
            </button>
          </footer>
        </div>
      </div>
    </Transition>
  </section>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
  type ComponentPublicInstance
} from "vue";

import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";

import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Alert from "~/components/AlertMessage.vue";
import ToolTip from "~/components/ToolTip.vue";
import type { WhatItCosts } from "~/server/resolvers/resolvers-types";
import {
  formatAcademicYearLabel,
  formatCurrency,
  formatTermLabel,
  residencyAffectsCost,
  shouldDisplayTransferCredits,
  toCollapseItems
} from "~/src/services/TuitionCalculatorFormService";
import type { FaqLinkTarget, TuitionCalculatorSelections } from "~/types/tuition-calculator";
import type { SemesterTotalsByYear, TuitionYearEstimate } from "~/types/types";
import { isExternalUrl, openExternalLinksInNewTab } from "~/utils";

import CostCollapseV2 from "./CostCollapseV2.vue";

const analyticsRegion = "main content";
const analyticsSection = "your estimated academic year total:";
const analyticsComponent = "asu online tuition calculator v2";

interface SavingsAppliedItem {
  title: string;
  amount: string;
}

const props = defineProps<{
  estimates: TuitionYearEstimate[];
  semesterTotals: SemesterTotalsByYear;
  activeAcademicYear: number;
  selections: TuitionCalculatorSelections;
  editing: boolean;
  pending: boolean;
  requestError: string | null;
  pageData?: Partial<WhatItCosts>;
}>();

const emit = defineEmits<{
  (event: "select-year", academicYear: number): void;
  (event: "include-summer", includeSummer: boolean): void;
  (event: "edit" | "cancel" | "recalculate" | "retry"): void;
  (event: "collapse-open" | "collapse-close", title: string): void;
  (event: "faq-link", target: FaqLinkTarget): void;
}>();

const handleFaqLink = (event: MouseEvent): void => {
  const anchor = (event.target as HTMLElement | null)?.closest("a");
  if (!anchor || isExternalUrl(anchor.getAttribute("href") ?? "")) return;
  event.preventDefault();
  emit("faq-link", "fees");
};

const yearTabRefs = ref<HTMLButtonElement[]>([]);
const editSelectionsButtonRef = ref<HTMLButtonElement | null>(null);
const editHeadingRef = ref<HTMLHeadingElement | null>(null);
const mobileDrawerCloseButtonRef = ref<HTMLButtonElement | null>(null);
const tuitionBannerMessageRef = ref<HTMLElement | null>(null);
const isMobileEditViewport = ref(false);
const isSavingsExpanded = ref(true);
const mobileDrawerTitleId = "tuition-results-mobile-edit-title";
const savingsAppliedButtonId = "tuition-results-savings-applied-trigger";
const savingsAppliedRegionId = "tuition-results-savings-applied-region";
const activeYearIdx = ref<number>(0);
let previousBodyOverflow: string | null = null;

const creditsPerSemesterTooltipText = computed(() => {
  const credits = props.selections.creditsPerSemester;
  if (credits <= 0) return "your selected credits";
  return `${credits} ${credits === 1 ? "credit" : "credits"}`;
});

const showTransferCredits = computed(() => shouldDisplayTransferCredits(props.selections));

const completionTimeTooltip = computed(() => {
  if (props.selections.degreeType === "Graduate") {
    return (
      "For graduate programs, course availability and scheduling may affect " +
      "your completion time. If you want to learn more about your potential " +
      "timeline, select “Request info” at the top right of the page to " +
      "connect with an enrollment coach."
    );
  }

  const baseTooltip =
    `If you take ${creditsPerSemesterTooltipText.value} each semester, ` +
    "you can complete your degree in this time.";

  if ((props.selections.transferCredits ?? 0) > 0) {
    return (
      `${baseTooltip} ` + "This time estimate accounts for your expected transfer credit total."
    );
  }

  return baseTooltip;
});

const estimatedTotalCostTooltip =
  "This is only an estimated cost based on today’s tuition costs. Your final " +
  "total cost will be determined based on how many courses you take, transfer " +
  "credits we accept and the tuition cost for each academic year.";

const numericSelectionValue = (value: number | null | undefined): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const formatSavingsDeduction = (value: number): string => {
  if (value > 0) return `-${currencyOrZero(value)}`;
  if (value < 0) return currencyOrZero(value);
  return "$0";
};

const savingsAppliedItems = computed<SavingsAppliedItem[]>(() => {
  const grants = numericSelectionValue(props.selections.grants);
  const scholarships = numericSelectionValue(props.selections.scholarships);
  const employerBenefit = numericSelectionValue(props.selections.employerTuitionBenefit);
  const aidValue =
    props.selections.degreeType === "Undergraduate" ? grants + scholarships : scholarships;

  return [
    {
      title:
        props.selections.degreeType === "Undergraduate"
          ? "Grants and scholarships"
          : "Scholarships",
      amount: formatSavingsDeduction(aidValue)
    },
    {
      title: "Employer tuition benefits",
      amount: formatSavingsDeduction(employerBenefit)
    }
  ];
});

const updateMobileEditViewport = (): void => {
  if (typeof window === "undefined") return;
  isMobileEditViewport.value = window.innerWidth < 992;
};

const setBodyScrollLock = (locked: boolean): void => {
  if (typeof document === "undefined") return;
  if (locked && previousBodyOverflow === null) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  } else if (!locked && previousBodyOverflow !== null) {
    document.body.style.overflow = previousBodyOverflow;
    previousBodyOverflow = null;
  }
};

// Cancelling or successfully committing an edit swaps the edit form back out
// for the committed summary without a route/view change, so the Transition
// in CostCalculatorV2 never fires to manage focus. Without this, the
// focused Cancel/Recalculate button is removed from the DOM and focus is
// silently dropped to <body>. Return it to the trigger that reopens editing.
watch(
  () => props.editing,
  async (editing, wasEditing) => {
    if (!wasEditing || editing) return;
    await nextTick();
    editSelectionsButtonRef.value?.focus({ preventScroll: true });
  }
);

const showInlineEdit = computed(() => props.editing && !isMobileEditViewport.value);
const showCommittedSummary = computed(() => !props.editing || isMobileEditViewport.value);
const showMobileEditDrawer = computed(() => props.editing && isMobileEditViewport.value);

watch(
  showMobileEditDrawer,
  async (isOpen) => {
    setBodyScrollLock(isOpen);
    if (!isOpen) return;
    await nextTick();
    mobileDrawerCloseButtonRef.value?.focus({ preventScroll: true });
  },
  { flush: "post" }
);

watch(showInlineEdit, async (isOpen) => {
  if (!isOpen) return;
  await nextTick();
  editHeadingRef.value?.focus({ preventScroll: true });
});

const selectedEstimate = computed(
  () =>
    props.estimates.find((estimate) => estimate.academic_year === props.activeAcademicYear) ??
    props.estimates[0]
);

const hasSavingsApplied = computed(() => {
  const savings = selectedEstimate.value?.savings_applied ?? "";
  const amount = Number(String(savings).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(amount) && amount > 0;
});

const selectedAcademicYear = computed(() => selectedEstimate.value?.academic_year);

// Only the selected year's panel is rendered, so every tab controls that one
// panel. Pointing tabs at their own year's id would dangle for unselected
// years whose panel is absent from the DOM.
const activePanelId = computed(() =>
  selectedAcademicYear.value !== undefined ? yearPanelId(selectedAcademicYear.value) : undefined
);

const showResidency = computed(() =>
  Boolean(residencyAffectsCost(props.selections) && props.selections.residencyStatus)
);

const residencyLabel = computed(() =>
  props.selections.residencyStatus === "res"
    ? "Arizona resident"
    : props.selections.residencyStatus === "nores"
      ? "Out-of-state resident"
      : ""
);

const currencyOrZero = (value: unknown): string => formatCurrency(value) ?? "$0";

const numericCurrencyValue = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== "string" || value.trim() === "") return null;

  const parsed = Number(value.trim().replace(/[$,]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const wholeCurrencyOrZero = (value: unknown): string => {
  const numericValue = numericCurrencyValue(value);
  return numericValue === null ? "$0" : (formatCurrency(Math.round(numericValue)) ?? "$0");
};

const roundedUpCurrencyOrZero = (value: unknown): string => {
  const numericValue = numericCurrencyValue(value);
  return numericValue === null ? "$0" : (formatCurrency(Math.ceil(numericValue)) ?? "$0");
};

const disclaimer = computed(() => props.pageData?.tcDisclaimer ?? props.pageData?.tcFootnote ?? "");

const tuitionBanner = computed(() => {
  const banner = props.selections.program?.tuitionBanner;
  return typeof banner === "string" ? banner.trim() : "";
});

const prepareTuitionBannerLinks = (): void => {
  const banner = tuitionBannerMessageRef.value;
  if (!banner) return;
  banner.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((anchor) => {
    anchor.classList.add("non-rds-link");
  });
  openExternalLinksInNewTab(banner);
};

watch(
  tuitionBanner,
  () => {
    void nextTick(prepareTuitionBannerLinks);
  },
  { immediate: true, flush: "post" }
);

const yearTabId = (academicYear: number): string => `tuition-results-year-${academicYear}`;
const yearPanelId = (academicYear: number): string => `tuition-results-panel-${academicYear}`;
const yearPanelHeadingId = (academicYear: number): string =>
  `tuition-results-heading-${academicYear}`;
const collapseId = (academicYear: number, term: string): string =>
  `tuition-results-${academicYear}-${term}`;

const setYearTabRef = (element: Element | ComponentPublicInstance | null, index: number): void => {
  if (element instanceof HTMLButtonElement) yearTabRefs.value[index] = element;
};

const trackYearSelect = (academicYear: number): void => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "pagination",
    analyticsRegion,
    analyticsSection,
    formatAcademicYearLabel(academicYear).toLowerCase(),
    analyticsComponent
  );
};

const selectYear = (academicYear: number, idx: number): void => {
  trackYearSelect(academicYear);
  activeYearIdx.value = idx;
  emit("select-year", academicYear);
};

const moveYearFocus = (
  currentIndex: number,
  direction: "previous" | "next" | "first" | "last"
): void => {
  const last = props.estimates.length - 1;
  if (last < 0) return;
  const next =
    direction === "first"
      ? 0
      : direction === "last"
        ? last
        : direction === "next"
          ? (currentIndex + 1) % props.estimates.length
          : (currentIndex - 1 + props.estimates.length) % props.estimates.length;
  const nextAcademicYear = props.estimates[next].academic_year;
  trackYearSelect(nextAcademicYear);
  emit("select-year", nextAcademicYear);
  void nextTick(() => yearTabRefs.value[next]?.focus());
};

const handleYearKeydown = (event: KeyboardEvent, index: number): void => {
  if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
    event.preventDefault();
    moveYearFocus(index, "previous");
  } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
    event.preventDefault();
    moveYearFocus(index, "next");
  } else if (event.key === "Home") {
    event.preventDefault();
    moveYearFocus(index, "first");
  } else if (event.key === "End") {
    event.preventDefault();
    moveYearFocus(index, "last");
  }
};

const termAmount = (academicYear: number, term: "fall" | "spring" | "summer"): string =>
  currencyOrZero(props.semesterTotals[String(academicYear)]?.[term]);

const handleIncludeSummerChange = (event: Event): void => {
  const checked = (event.target as HTMLInputElement).checked;
  analyticsComposable.trackSelectEvent(
    "onclick",
    checked ? "click" : "unclick",
    "checkbox",
    analyticsRegion,
    analyticsSection,
    "include summers",
    analyticsComponent
  );
  emit("include-summer", checked);
};

const trackTermCollapse = (action: "open" | "close", title: string): void => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    action,
    "click",
    analyticsRegion,
    analyticsSection,
    title.toLowerCase(),
    analyticsComponent
  );
};

const handleCollapseOpen = (title: string): void => {
  trackTermCollapse("open", title);
  emit("collapse-open", title);
};

const handleCollapseClose = (title: string): void => {
  trackTermCollapse("close", title);
  emit("collapse-close", title);
};

const toggleSavingsApplied = (): void => {
  isSavingsExpanded.value = !isSavingsExpanded.value;
  const action = isSavingsExpanded.value ? "open" : "close";
  trackTermCollapse(action, "Savings applied");
  if (action === "open") {
    emit("collapse-open", "Savings applied");
  } else {
    emit("collapse-close", "Savings applied");
  }
};

const animate = ref(false);

onMounted(() => {
  updateMobileEditViewport();
  window.addEventListener("resize", updateMobileEditViewport);
  // Wait one frame so the browser paints the initial state first.
  requestAnimationFrame(() => {
    animate.value = true;
  });
});

onUnmounted(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", updateMobileEditViewport);
  }
  setBodyScrollLock(false);
});
</script>

<style scoped lang="scss">
.result-loader {
  inset: 0;
  z-index: 1;
  align-items: flex-start;
  box-sizing: border-box;
  min-height: 100%;
  padding-top: 42px;
  background: rgba(255, 255, 255, 0.82);
}

.result-loader__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 278px;
  height: 170px;
  padding: 31px 16px 21px;
  background: #fff;
  box-shadow: 0 0 4.8px rgba(0, 0, 0, 0.1);
}

.result-loader__title {
  color: #000;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5rem;
  text-align: center;
}

.result-loader__spinner {
  position: relative;
  width: 58px;
  height: 58px;
  margin-top: 11px;
  animation: result-loader-spin 1s linear infinite;
}

.result-loader__spinner span {
  position: absolute;
  border-radius: 50%;
  background: var(--rds-secondary, #ffc627);
}

.result-loader__spinner span:nth-child(1) {
  top: 10px;
  left: 18px;
  width: 5px;
  height: 5px;
  opacity: 0.55;
}

.result-loader__spinner span:nth-child(2) {
  top: 14px;
  left: 27px;
  width: 8px;
  height: 8px;
  opacity: 0.75;
}

.result-loader__spinner span:nth-child(3) {
  top: 24px;
  left: 33px;
  width: 10px;
  height: 10px;
}

.result-loader__spinner span:nth-child(4) {
  top: 36px;
  left: 28px;
  width: 8px;
  height: 8px;
  opacity: 0.85;
}

.result-loader__spinner span:nth-child(5) {
  top: 43px;
  left: 19px;
  width: 4px;
  height: 4px;
  opacity: 0.45;
}

@keyframes result-loader-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .result-loader__spinner {
    animation: none;
  }
}

dl > div {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

dd {
  margin-bottom: 0;
  text-align: right;
}

.results-overview__metrics {
  gap: 0.75rem;
}

.result-edit-button.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-color: var(--rds-dark-3, #191919);
  border-radius: 999px;
  background: #fff;
  color: var(--rds-dark-3, #191919);
}

.result-edit-button.btn:hover,
.result-edit-button.btn:focus-visible {
  border-color: var(--rds-dark-3, #191919);
  background: #f1f1f1;
  color: var(--rds-dark-3, #191919);
}

.result-mobile-drawer {
  display: none;
}

.results-headline {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.results-headline__label {
  margin-bottom: 0.25rem;
}

.results-headline__amount {
  color: var(--rds-green, #78be20);
  font-weight: 700;
  font-size: clamp(2rem, 5vw, 3rem);
  line-height: 1;
  margin-bottom: 0;
}

.results-headline__boxes {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.results-box {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 10rem;
  padding: 0.75rem 2rem 0.75rem 1rem;
  border: 1px solid var(--rds-border-color, #d8d8d8);
  border-radius: 0.25rem;
  background: var(--rds-light-2, #e8e8e8);
}

.result-metric {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-right: 2.5rem !important;
}

.result-metric__icon {
  flex: 0 0 auto;
  width: 1.5rem;
  color: var(--rds-dark-2, #747474);
  font-size: 1.5rem;
}

.result-metric__icon--calendar,
.result-metric__icon--dollar {
  width: 16px;
  height: 16px;
}

.result-metric__text {
  color: var(--rds-dark-2, #747474);
}

.result-metric__icon--mobile {
  display: none;
}

.result-metric__value {
  display: inline;
}

.result-metric__nowrap {
  white-space: nowrap;
}

.result-metric__tooltip {
  position: absolute;
  top: 50%;
  right: 0.75rem;
  display: inline-flex;
  margin-top: -0.625rem;
}

.result-metric__tooltip :deep(.tool-tip-icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  color: var(--rds-primary, #8c1d40);
  font-size: 1rem;
  line-height: 1;
}

.result-metric__tooltip :deep(.tool-tip-floating) {
  overflow: visible;
  width: min(16.25rem, calc(100vw - 2rem)) !important;
  max-width: min(16.25rem, calc(100vw - 2rem)) !important;
  padding: 0;
  border-radius: 0;
  background: transparent !important;
  color: #fff;
}

.result-metric__tooltip-content {
  position: relative;
  padding: 1rem;
  background: var(--rds-dark-3, #191919);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.45;
}

.result-metric__info-icon {
  display: block;
}

.tuition-banner {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  border: 1px solid var(--rds-warning, #ff7f32);
  background: color-mix(in srgb, var(--rds-warning, #ff7f32) 16%, white);
  padding: 1rem;
  color: var(--rds-dark-3, #191919);
  font-size: 1rem;
  line-height: 1.5;
}

.tuition-banner:focus-visible {
  outline: 2px solid var(--rds-primary);
  outline-offset: 2px;
}

.tuition-banner__icon {
  flex: 0 0 auto;
  width: 1.5rem;
  height: 1.5rem;
  color: var(--rds-dark-3, #191919);
  font-size: 1.5rem;
}

.tuition-banner__message {
  flex: 1 1 auto;

  :deep(p:last-child) {
    margin-bottom: 0;
  }
}

.result-totals {
  margin-bottom: 1rem;
}

.result-total {
  display: flex;
  justify-content: space-between;
}

.result-savings-collapse {
  display: block;
  margin: 0.25rem 0 0.75rem;
  box-shadow: 0 0.25rem 0.75rem rgba(25, 25, 25, 0.12);
}

.result-savings-toggle {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: 0;
  font-weight: 700;
  text-align: left;
}

.result-savings-toggle__label,
.result-savings-toggle__amount {
  position: relative;
  z-index: 1;
}

.result-savings-toggle__label {
  display: inline-flex;
  align-items: center;
}

.result-savings-toggle__icon {
  width: 16px;
  height: 16px;
  font-size: 1.25rem;
}

.result-savings-body {
  padding: 1.5rem 1.5rem 1.25rem;
  border: 1px solid var(--rds-light-2, #e8e8e8);
  border-top: 0;
  background: #fff;
}

.result-savings-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--rds-dark-2, #747474);
}

.result-savings-item + .result-savings-item {
  border-top: 1px solid var(--rds-border-color, #d8d8d8);
}

.result-savings-item__amount {
  color: var(--rds-dark-3, #4d4d4d);
  white-space: nowrap;
}

.result-total--savings {
  // background: var(--rds-green, #78be20);
  color: #fff;
  // border-radius: 0.25rem;
}

.result-total--savings dt,
.result-total--savings dd {
  color: inherit;
}

.result-total--estimated dd {
  font-weight: 700;
}

.result-fin-aid {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.result-fin-aid svg {
  flex: 0 0 auto;
}

.result-disclaimer {
  font-size: 1rem;
  border: 1px solid var(--rds-light-4);
  text-align: center;

  :deep(a) {
    color: var(--rds-primary, #8c1d40);
    font-weight: 700;
    text-decoration: underline;
  }
}
.accent-dark {
  border-color: var(--rds-light-5, #bfbfbf);
  accent-color: var(--rds-dark-3, #191919);
}

.form-check-input:checked {
  background-color: var(--rds-secondary);
  border-color: var(--rds-secondary);
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%23191919' d='M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z'/%3e%3c/svg%3e");
  background-size: 66%;
}

.animated-box {
  position: relative;
  overflow: hidden;

  background: transparent;
  color: black;
  transition: color 1000ms ease;
}

.animated-box::before {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--rds-success, #446d12);

  transform: scaleX(0);
  transform-origin: left;
  transition: transform 1000ms ease;

  // z-index: -1;
}

.animated-box.active {
  color: white;
}

.animated-box.active::before {
  transform: scaleX(1);
}

@media (max-width: 991.98px) {
  .result-mobile-drawer {
    position: fixed;
    inset: 0;
    z-index: 1050;
    display: flex;
    align-items: flex-end;
    justify-content: stretch;
  }

  .result-mobile-drawer__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(25, 25, 25, 0.58);
  }

  .result-mobile-drawer__panel {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: calc(100dvh - 4rem);
    max-height: 100dvh;
    overflow: hidden;
    border-radius: 1rem 1rem 0 0;
    background: #fff;
    box-shadow: 0 -0.75rem 2rem rgba(25, 25, 25, 0.2);
  }

  .result-mobile-drawer__header {
    position: relative;
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    min-height: 6rem;
    padding: 1.25rem 5rem 1.25rem 1.5rem;
  }

  .result-mobile-drawer__header h2 {
    color: var(--rds-dark-2, #484848);
    font-weight: 700;
    text-align: center;
  }

  .result-mobile-drawer__close {
    position: absolute;
    top: 1.125rem;
    right: 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    padding: 0;
    border: 1px solid var(--rds-border-color, #d8d8d8);
    border-radius: 50%;
    background: #fff;
    color: var(--rds-dark-3, #191919);
    font-size: 1.5rem;
  }

  .result-mobile-drawer__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 0 1.5rem 1.5rem;
    -webkit-overflow-scrolling: touch;
  }

  .result-mobile-drawer__footer {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    padding: 0.75rem 1.5rem calc(0.75rem + env(safe-area-inset-bottom));
    border-top: 1px solid var(--rds-border-color, #d8d8d8);
    background: #fff;
  }

  .result-mobile-drawer :deep(.result-edit-form__group),
  .result-mobile-drawer :deep(.result-edit-form__field) {
    margin-bottom: 1.75rem;
  }

  .result-mobile-drawer :deep(.result-edit-form__group legend),
  .result-mobile-drawer :deep(.result-edit-form__field label) {
    color: var(--rds-dark-2, #484848);
    font-size: 1.25rem;
    font-weight: 700;
  }

  .result-mobile-drawer :deep(.result-edit-form__derived) {
    margin-top: 0.75rem;
    font-size: 1.25rem;
  }

  .result-mobile-drawer :deep(.result-edit-form__number),
  .result-mobile-drawer :deep(.typeahead__dropdown-toggle) {
    min-height: 3.125rem;
  }

  .result-mobile-drawer :deep(.form-control),
  .result-mobile-drawer :deep(.typeahead__search),
  .result-mobile-drawer :deep(.typeahead__selected),
  .result-mobile-drawer :deep(.typeahead__dropdown-option),
  .result-mobile-drawer :deep(.result-edit-form__unit) {
    font-size: 1.25rem;
  }

  .result-mobile-drawer-enter-active,
  .result-mobile-drawer-leave-active {
    transition: opacity 220ms ease;
  }

  .result-mobile-drawer-enter-active .result-mobile-drawer__panel,
  .result-mobile-drawer-leave-active .result-mobile-drawer__panel {
    transition: transform 260ms ease;
  }

  .result-mobile-drawer-enter-from,
  .result-mobile-drawer-leave-to {
    opacity: 0;
  }

  .result-mobile-drawer-enter-from .result-mobile-drawer__panel,
  .result-mobile-drawer-leave-to .result-mobile-drawer__panel {
    transform: translateY(100%);
  }

  .results-overview {
    padding-bottom: 2.5rem !important;
    margin-bottom: 2.5rem !important;
  }

  .results-overview__content {
    flex-direction: column;
  }

  .results-overview__metrics {
    width: 100%;
    gap: 1rem;
  }

  .result-metric {
    min-height: 5.75rem;
    padding: 1.25rem 4rem 1.25rem 2.5rem !important;
  }

  .tuition-banner {
    align-items: flex-start;
  }

  .result-metric__text {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.75rem;
  }

  .result-metric__text .fw-bold {
    color: var(--rds-dark-3, #191919);
    font-size: 1.25rem;
    white-space: nowrap;
  }

  .result-summary-list {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    margin-bottom: 2.5rem !important;
  }

  .result-summary-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0 !important;
  }

  .result-summary-row--residency {
    order: 1;
  }

  .result-summary-row--degree {
    order: 2;
  }

  .result-summary-row--program {
    order: 3;
  }

  .result-summary-row--summer {
    order: 4;
  }

  .result-summary-row--transfer {
    order: 5;
  }

  .result-summary-row--credits {
    order: 6;
  }

  .result-summary-row--grants {
    order: 7;
  }

  .result-summary-row--scholarships {
    order: 8;
  }

  .result-summary-row--employer {
    order: 9;
  }

  .result-summary-label,
  .result-summary-value {
    margin-bottom: 0;
    font-size: 1.25rem;
    line-height: 1.35;
  }

  .result-summary-label {
    flex: 1 1 45%;
    min-width: 0;
    color: var(--rds-dark-2, #747474);
    font-weight: 700;
  }

  .result-summary-value {
    flex: 1 1 45%;
    min-width: 0;
    color: var(--rds-dark-3, #191919);
    text-align: right;
  }

  .result-summary-value--program {
    max-width: 55%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .result-summary-value--programtitle {
    max-width: 55%;
    overflow-wrap: anywhere;
  }

  .result-edit-button.btn {
    width: 100%;
    padding: 1rem 1.5rem;
    font-size: 1.5rem;
  }
}

@media (max-width: 575.98px) {
  .result-loader__card {
    width: 190px;
  }

  .result-mobile-drawer__panel {
    height: calc(100dvh - 3rem);
    border-radius: 0.875rem 0.875rem 0 0;
  }

  .result-mobile-drawer__header {
    min-height: 5.25rem;
    padding-right: 4.5rem;
  }

  .result-mobile-drawer__body {
    padding-right: 1rem;
    padding-left: 1rem;
  }

  .result-mobile-drawer__footer {
    padding-right: 1rem;
    padding-left: 1rem;
  }

  .result-metric {
    gap: 0.75rem;
    padding: 1rem 2.5rem 1rem 1rem !important;
  }

  .result-metric__icon {
    width: 1.25rem;
    font-size: 1.25rem;
  }

  .result-metric__text {
    gap: 0.5rem;
    font-size: 0.9375rem;
  }

  .result-metric__text .fw-bold {
    font-size: 1.125rem;
  }

  .result-summary-label,
  .result-summary-value {
    font-size: 1.125rem;
  }

  .result-edit-button.btn {
    font-size: 1.25rem;
  }
}

@media (max-width: 419.98px) {
  .result-metric {
    min-height: 5.625rem;
    justify-content: center;
    padding: 1rem 3rem !important;
    text-align: center;
  }

  .result-metric__text {
    flex-direction: column;
    align-items: flex-start;
    justify-content: left;
    gap: 0.5rem;
    width: 100%;
    line-height: 1.4;
  }

  .result-metric__label,
  .result-metric__value {
    justify-content: center;
  }

  .result-metric__icon--desktop {
    display: none;
  }

  .result-metric__icon--mobile {
    display: inline-block;
  }

  .result-metric__value {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    line-height: 1.2;
  }

  .result-metric__tooltip {
    right: 1rem;
  }
}
.years-btn {
  background-color: transparent;
  color: var(--rds-dark-3, #191919);
  padding: 3px 0;
  margin-right: 1.5rem;
  border: none;
  -webkit-text-fill-color: var(--rds-dark-3, #191919);
}
.active-year {
  border-bottom: 6px solid var(--rds-secondary, #ffc627) !important;
  border-radius: 0;
  color: var(--rds-dark-3, #191919);
  -webkit-text-fill-color: var(--rds-dark-3, #191919);
}

/* Prevent iOS Safari from auto-detecting the year range as a date link */
.year-label {
  color: inherit;
  pointer-events: none;
  -webkit-text-fill-color: inherit;
}
</style>
